import React, { useCallback, useEffect, memo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Flex,
  IconButton,
  Icon,
  HStack,
  Tooltip,
  WrapItem,
  Menu,
  MenuItem,
  MenuList,
  MenuButton,
} from "@chakra-ui/react";
import { FaEllipsisV, FaPlus, FaListUl } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import SocialAccountPropType from "../../../_types/SocialAccount";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";

const SocialAccountsList = ({
  addConnectionUrl,
  onAddConnection,
  selected = [],
  accounts,
  multiple,
  required,
  disabled,
  readonly,
  onChange,
  onToggleFullList,
}) => {
  const { t } = useTranslation(["planner"]);
  const setSelected = useCallback(
    (ids = []) => {
      if (!readonly && ids?.concat(",") !== selected?.concat(",")) {
        onChange?.(ids);
      }
    },
    [readonly, selected, onChange]
  );

  const handleClick = useCallback(
    (item) => () => {
      const index = (selected || []).indexOf(item.id);

      if (!multiple) {
        if (index === -1) {
          setSelected([item.id]);
        } else if (!required) {
          setSelected([]);
        }
      } else if (index === -1) {
        setSelected([...selected, item.id]);
      } else if (!required || (selected && selected.length > 1)) {
        setSelected([
          ...selected.slice(0, index),
          ...selected.slice(index + 1),
        ]);
      }
    },
    [selected, required, multiple, setSelected]
  );

  useEffect(() => {
    if (
      required &&
      accounts &&
      accounts.length &&
      (!selected || !selected.length)
    ) {
      setSelected([accounts[0].id]);
    } else if (!multiple && selected && selected.length > 1) {
      setSelected([selected[0]]);
    }
  }, [accounts, required, multiple, selected, setSelected]);

  return (
    <Flex maxWidth="100%">
      <HStack
        overflowX="auto"
        overflowY="hidden"
        as="ul"
        m="0"
        spacing="2"
        p="0"
        pb="1"
        pr="2"
      >
        {accounts.map((item) => {
          const isSelected = selected?.includes(item.id);
          const tooltipLabel = (
            <Box>
              <Box>{item.name}</Box>
              <Box fontSize="0.9em" fontWeight="normal">{`${t(
                `social_types.${item.social_type}.full`
              )}`}</Box>
            </Box>
          );

          return (
            <WrapItem key={item.id} as="li">
              <Tooltip
                label={tooltipLabel}
                placement="top"
                hasArrow
                textAlign="center"
              >
                <SocialAccountAvatar
                  onClick={onChange && !readonly ? handleClick(item) : null}
                  disabled={disabled}
                  name={item.name}
                  src={item.image}
                  size="sm"
                  minWidth="initial"
                  selectable
                  selected={isSelected}
                  socialType={item.social_type}
                  socialTypeShown
                  hasError={item.active === "0"}
                />
              </Tooltip>
            </WrapItem>
          );
        })}
      </HStack>
      {(onAddConnection || onToggleFullList) && (
        <Menu isLazy minWidth="6" autoSelect={false}>
          <MenuButton
            bg="gray.500"
            as={IconButton}
            icon={<FaEllipsisV />}
            aria-label="Options"
            onClick={(e) => e.stopPropagation()}
            isRound
            size="sm"
          />
          <MenuList
            minW="140px"
            fontSize="sm"
            onClick={(e) => e.stopPropagation()}
          >
            {onAddConnection && (
              <MenuItem
                onClick={onAddConnection}
                icon={<Icon fontSize="sm" as={FaPlus} />}
              >
                {t("accounts.add")}
              </MenuItem>
            )}
            {onToggleFullList && (
              <MenuItem
                onClick={onToggleFullList}
                icon={<Icon fontSize="sm" as={FaListUl} />}
              >
                {t("accounts.open_full_list")}
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      )}
      {addConnectionUrl && (
        <Tooltip
          label={t("accounts.add")}
          placement="top"
          hasArrow
          textAlign="center"
        >
          <IconButton
            bg="gray.600"
            icon={<Icon as={FaPlus} />}
            aria-label={t("accounts.add")}
            as="a"
            href={addConnectionUrl}
            target="_blank"
            isRound
            size="sm"
            sx={{
              _hover: {
                color: "gray.100",
                bg: "gray.400",
              },
            }}
          />
        </Tooltip>
      )}
    </Flex>
  );
};

SocialAccountsList.propTypes = {
  addConnectionUrl: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.string.isRequired),
  accounts: PropTypes.arrayOf(SocialAccountPropType).isRequired,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  onChange: PropTypes.func,
  onToggleFullList: PropTypes.func,
};

SocialAccountsList.defaultProps = {
  addConnectionUrl: undefined,
  selected: undefined,
  multiple: undefined,
  required: undefined,
  disabled: undefined,
  readonly: undefined,
  onChange: undefined,
  onToggleFullList: undefined,
};

export default memo(SocialAccountsList);
